import { useEffect, useState } from "react";
import { LocaleScope, SUPPORTED_LANGUAGES, useAPI, useTranslation } from "@humansignal/core";

/**
 * Route-scoped locale override for annotator-facing pages.
 *
 * Per-project setting: when ``Project.annotator_language`` is set, the
 * labeling and review streams render in that language for every viewer
 * (so a Hausa sentiment project's annotators see the labeling chrome
 * in Hausa even if they signed up with English in their profile).
 * Management chrome — Settings, Org, Platform, Project Dashboard —
 * keeps using the viewer's personal preference and is unaffected.
 *
 * Two ways to feed it:
 *   • ``project`` — a project object you already have in scope (e.g.
 *     from ``useProject()``); zero extra HTTP traffic.
 *   • ``projectId`` — fall-through fetch for routes that don't sit
 *     inside ProjectProvider (review-stream is one). Hits the standard
 *     ``project`` endpoint via ``useAPI()``.
 *
 * If neither is supplied, or the project's ``annotator_language`` is
 * empty (the default), this component is a transparent pass-through —
 * the parent LocaleProvider's language wins, which means the user's
 * own preference is honoured as before.
 */
export const ProjectLocaleScope = ({
  project,
  projectId,
  children,
}: {
  project?: { annotator_language?: string | null } | null;
  projectId?: number | string | null;
  children: React.ReactNode;
}) => {
  const api = useAPI();
  const [fetched, setFetched] = useState<{ annotator_language?: string | null } | null>(null);

  // Fall-through fetch when only projectId was provided. Skipped if a
  // ``project`` prop is already in scope — the caller knows better.
  useEffect(() => {
    if (project) return;
    if (!projectId) return;
    let alive = true;
    api.callApi("project", { params: { pk: projectId }, suppressError: true }).then((res: any) => {
      if (!alive) return;
      if (res && !res.error) setFetched(res);
    });
    return () => {
      alive = false;
    };
  }, [project, projectId, api]);

  const source = project ?? fetched ?? null;
  const override = source?.annotator_language || null;
  return (
    <LocaleScope override={override}>
      {override ? <ProjectLocaleBadge code={override} /> : null}
      {children}
    </LocaleScope>
  );
};

// Sticky one-liner that explains *why* the labeling chrome is showing
// in a different language than the user's profile preference. Lives
// inside the scope so the badge text itself is also localised to the
// project's language (a Hausa-project annotator reads the badge in
// Hausa, an English fallback reader reads it in English). Hidden
// after first dismiss for the rest of the session via sessionStorage —
// the operator gets the heads-up once per browser tab, then the
// surface goes quiet so it doesn't crowd the labeling chrome.
const BADGE_DISMISS_KEY = "afri:projlang:dismissed";
const ProjectLocaleBadge = ({ code }: { code: string }) => {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(() => {
    try {
      return window.sessionStorage.getItem(`${BADGE_DISMISS_KEY}:${code}`) === "1";
    } catch {
      return false;
    }
  });
  if (dismissed) return null;
  const meta = SUPPORTED_LANGUAGES.find((l) => l.code === code);
  const langLabel = meta ? `${meta.endonym} (${meta.name})` : code.toUpperCase();
  const onClose = () => {
    try {
      window.sessionStorage.setItem(`${BADGE_DISMISS_KEY}:${code}`, "1");
    } catch {
      /* ignore */
    }
    setDismissed(true);
  };
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "8px 14px",
        background: "var(--color-primary-surface, #f0f5ff)",
        border: "1px solid var(--color-primary-border, #c0d1ff)",
        borderRadius: 6,
        margin: "0 0 12px",
        fontSize: "var(--font-size-body-small, 13px)",
        color: "var(--color-neutral-content, #1f2937)",
      }}
    >
      <span style={{ flex: 1 }}>{t("i18n.project_locale.badge", { lang: langLabel })}</span>
      <button
        type="button"
        onClick={onClose}
        aria-label={t("common.dismiss", undefined, "Dismiss")}
        style={{
          background: "transparent",
          border: "none",
          cursor: "pointer",
          fontSize: "var(--font-size-title-md)",
          lineHeight: 1,
          color: "inherit",
          padding: "2px 6px",
        }}
      >
        ×
      </button>
    </div>
  );
};
